import { PublicClientApplication } from '@azure/msal-browser';
import {
  getEntraLoginRequest,
  getMsalConfig,
  isEntraConfigured,
} from './entraConfig';

let instance = null;
let initPromise = null;
/** MSAL requires handleRedirectPromise once per page load before any interactive API. */
let redirectPromise = null;

export function getMsalInstance() {
  if (!isEntraConfigured()) {
    return null;
  }
  if (!instance) {
    instance = new PublicClientApplication(getMsalConfig());
    initPromise = instance.initialize();
  }
  return instance;
}

export async function ensureMsal() {
  const msal = getMsalInstance();
  if (!msal) {
    return null;
  }
  await initPromise;
  return msal;
}

export async function ensureRedirectHandled() {
  const msal = await ensureMsal();
  if (!msal) {
    return null;
  }
  if (!redirectPromise) {
    redirectPromise = msal.handleRedirectPromise();
  }
  return redirectPromise;
}

export async function beginEntraSignIn() {
  const msal = await ensureMsal();
  if (!msal) {
    throw new Error('Microsoft Entra is not configured');
  }
  await ensureRedirectHandled();
  try {
    await msal.loginRedirect(getEntraLoginRequest());
  } catch (err) {
    const code = err?.errorCode || err?.name || '';
    if (code !== 'interaction_in_progress') {
      throw err;
    }
    // Stale interaction flag (back button, cancelled redirect, double click).
    redirectPromise = msal.handleRedirectPromise();
    await redirectPromise;
    await msal.loginRedirect(getEntraLoginRequest());
  }
}

export async function completeEntraRedirect() {
  const msal = await ensureMsal();
  if (!msal) {
    return null;
  }
  const result = await ensureRedirectHandled();
  if (result?.account) {
    msal.setActiveAccount(result.account);
  }
  const account = msal.getActiveAccount() || msal.getAllAccounts()[0] || null;
  if (!account) {
    return result
      ? { accessToken: result.accessToken || '', account: result.account || null }
      : null;
  }
  if (result?.accessToken) {
    return { accessToken: result.accessToken, account };
  }
  const silent = await msal.acquireTokenSilent({
    ...getEntraLoginRequest(),
    account,
  });
  return { accessToken: silent.accessToken, account };
}

export async function logoutEntra() {
  const msal = await ensureMsal();
  if (!msal) {
    return false;
  }
  const account = msal.getActiveAccount() || msal.getAllAccounts()[0];
  if (!account) {
    return false;
  }
  await msal.logoutRedirect({ account });
  return true;
}
